'use client';

import { memo, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Stack,
  alpha,
  Chip,
  useMediaQuery,
  useTheme,
  keyframes,
} from '@mui/material';
import { AccountBalanceWallet, Menu as MenuIcon, Inventory2 } from '@mui/icons-material';
import { Typography } from './Typography';
import { themeColors } from '../theme/theme';
import { brandingConfig } from '@/config/branding';
import { useTranslation } from './useTranslation';

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.4), 0 0 16px rgba(16, 185, 129, 0.2); }
  50% { box-shadow: 0 0 16px rgba(16, 185, 129, 0.6), 0 0 24px rgba(16, 185, 129, 0.4); }
`;

const TopNav = memo(({ user, onAvatarClick, onMenuClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const router = useRouter();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greeting.morning');
    if (hour < 17) return t('greeting.afternoon');
    return t('greeting.evening');
  };

  const getGreetingEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '☀️';
    if (hour < 17) return '🌤️';
    return '🌙';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        // Match projectR: transparent glassmorphism top nav
        background: 'transparent',
        backdropFilter: 'blur(10px)',
        boxShadow: 'none',
      }}
    >
      <Toolbar
        sx={{
          minHeight: scrolled ? { xs: 60, sm: 68 } : { xs: 72, sm: 84 },
          px: { xs: 2.5, sm: 3.5 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Left: Menu + User Info */}
        <Stack direction="row" spacing={{ xs: 1.5, sm: 2.5 }} alignItems="center">
          <IconButton
            onClick={onMenuClick}
            sx={{
              color: 'text.primary',
              display: { xs: 'flex', sm: 'none' },
              width: 44,
              height: 44,
              background: alpha('#000', 0.08),
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha('#000', 0.12),
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            onClick={onAvatarClick}
            sx={{
              width: scrolled ? { xs: 120, sm: 140 } : { xs: 140, sm: 160 },
              height: scrolled ? { xs: 40, sm: 46 } : { xs: 46, sm: 52 },
              cursor: 'pointer',
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              // boxShadow: `0 4px 20px ${alpha('#000', 0.1)}`,
              overflow: 'hidden',
              '&:hover': {
                transform: 'scale(1.02)',
                // boxShadow: `0 6px 28px ${alpha('#000', 0.15)}`,
              },
            }}
          >
            <img
              src="/assets/terrakite-teal-logo.png"
              alt={brandingConfig.assets.logoAlt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              opacity: scrolled ? 0.95 : 1,
              transition: 'all 0.4s ease',
              transform: scrolled ? 'translateY(0)' : 'translateY(2px)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                type="caption"
                text={getGreeting()}
                style={{
                  color: alpha('#000', 0.7),
                  fontSize: scrolled ? 11 : 13,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  transition: 'font-size 0.3s ease',
                }}
              />
              <span style={{ fontSize: scrolled ? 14 : 16, transition: 'font-size 0.3s ease' }}>
                {getGreetingEmoji()}
              </span>
            </Stack>
            <Typography
              type="h6"
              text={user?.userName || t('greeting.guest')}
              style={{
                color: '#1a1a1a',
                fontWeight: 800,
                fontSize: scrolled ? 17 : 20,
                transition: 'font-size 0.3s ease',
                letterSpacing: 0.3,
                textShadow: `0 2px 8px ${alpha('#000', 0.2)}`,
              }}
            />
          </Box>
        </Stack>

        {/* Right: Quick Actions */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Booking Button: Mobile-only glowing shortcut */}
          {isMobile && (
            <Box sx={{ position: 'relative' }}>
              <IconButton
                onClick={() => router.push('/booking')}
                sx={{
                  color: '#fff',
                  bgcolor: '#10b981',
                  width: 44,
                  height: 44,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `${glowPulse} 2s ease-in-out infinite`,
                  '&:hover': {
                    bgcolor: '#059669',
                    transform: 'scale(1.08)',
                    animation: `${glowPulse} 1s ease-in-out infinite`,
                  },
                }}
                title="Quick Booking"
              >
                <Inventory2 sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}

          {/* Wallet Button: Only show for complete profiles (hide for incomplete business profiles) */}
          {user &&
            (!user.userType ||
              user.userType?.toLowerCase() !== 'business' ||
              user.businessSetupCompleted) && (
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => (window.location.href = '/wallet')}
                  sx={{
                    color: 'text.primary',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    backdropFilter: 'blur(6px)',
                    width: { xs: 44, sm: 48 },
                    height: { xs: 44, sm: 48 },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.08)',
                      transform: 'translateY(-2px) scale(1.03)',
                    },
                  }}
                >
                  <AccountBalanceWallet sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </IconButton>
              </Box>
            )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
});

TopNav.displayName = 'TopNav';

export { TopNav };
